import { useMemo, useRef, useState } from "react";
import { CaretUp, CaretDown } from "@phosphor-icons/react";
import { useUiPref } from "./useUiPref";

/**
 * Windows Explorer-style table: click header to sort (asc → desc), drag a
 * header to reorder columns, drag its right edge to resize. Alt+←/→ on a
 * focused header moves the column (keyboard alternative). Prefs persist.
 */
export function ExplorerTable({
  prefKey, columns, rows, rowKey = (r) => r.id, selectedId, onRowClick,
  defaultSort, testId, emptyText = "Nothing here yet.", dense = false,
}) {
  const fallback = useMemo(() => ({
    order: columns.map((c) => c.key),
    widths: Object.fromEntries(columns.map((c) => [c.key, c.width || 140])),
    sort: defaultSort || { key: columns[0].key, dir: "asc" },
  }), [columns, defaultSort]);
  const [pref, setPref] = useUiPref(prefKey, fallback);
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);
  const resizing = useRef(null);

  const byKey = Object.fromEntries(columns.map((c) => [c.key, c]));
  const order = [...(pref.order || []).filter((k) => byKey[k]), ...columns.map((c) => c.key).filter((k) => !(pref.order || []).includes(k))];
  const cols = order.map((k) => byKey[k]);
  const sort = pref.sort && byKey[pref.sort.key] ? pref.sort : fallback.sort;

  const sorted = useMemo(() => {
    const col = byKey[sort.key];
    if (!col) return rows;
    const get = col.sortValue || ((r) => r[col.key]);
    const out = [...rows].sort((a, b) => {
      const va = get(a) ?? ""; const vb = get(b) ?? "";
      if (typeof va === "number" && typeof vb === "number") return va - vb;
      return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
    });
    return sort.dir === "desc" ? out.reverse() : out;
  }, [rows, sort.key, sort.dir]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSort = (key) => {
    if (byKey[key].sortable === false) return;
    setPref({ sort: { key, dir: sort.key === key && sort.dir === "asc" ? "desc" : "asc" } });
  };
  const move = (from, to) => {
    if (from === to) return;
    const next = order.filter((k) => k !== from);
    next.splice(next.indexOf(to) + (order.indexOf(from) < order.indexOf(to) ? 1 : 0), 0, from);
    setPref({ order: next });
  };
  const moveBy = (key, delta) => {
    const i = order.indexOf(key); const j = i + delta;
    if (j < 0 || j >= order.length) return;
    const next = [...order]; [next[i], next[j]] = [next[j], next[i]];
    setPref({ order: next });
  };
  const startResize = (e, key) => {
    e.preventDefault(); e.stopPropagation();
    resizing.current = { key, x: e.clientX, w: pref.widths?.[key] || byKey[key].width || 140 };
    const onMove = (ev) => {
      const r = resizing.current; if (!r) return;
      const w = Math.max(byKey[r.key].minWidth || 60, r.w + ev.clientX - r.x);
      setPref((p) => ({ widths: { ...(p.widths || {}), [r.key]: w } }));
    };
    const onUp = () => { resizing.current = null; window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const totalW = cols.reduce((s, c) => s + (pref.widths?.[c.key] || c.width || 140), 0);

  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="text-sm border-collapse" style={{ tableLayout: "fixed", width: `max(100%, ${totalW}px)` }}>
        <colgroup>
          {cols.map((c) => <col key={c.key} style={{ width: pref.widths?.[c.key] || c.width || 140 }} />)}
        </colgroup>
        <thead className="sticky top-0 z-10">
          <tr className="bg-slate-50 border-b border-slate-200">
            {cols.map((c) => {
              const active = sort.key === c.key;
              return (
                <th
                  key={c.key}
                  draggable
                  onDragStart={(e) => { setDragKey(c.key); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", c.key); }}
                  onDragOver={(e) => { e.preventDefault(); if (overKey !== c.key) setOverKey(c.key); }}
                  onDragLeave={() => setOverKey(null)}
                  onDrop={(e) => { e.preventDefault(); move(dragKey || e.dataTransfer.getData("text/plain"), c.key); setDragKey(null); setOverKey(null); }}
                  onDragEnd={() => { setDragKey(null); setOverKey(null); }}
                  className={`relative select-none text-left font-semibold text-[11px] uppercase tracking-wider px-0 py-0 border-r border-slate-200 last:border-r-0 transition-colors ${active ? "text-[#1f64c2] bg-[#eaf2ff]" : "text-slate-500"} ${overKey === c.key && dragKey !== c.key ? "shadow-[inset_3px_0_0_#327de3]" : ""} ${dragKey === c.key ? "opacity-50" : ""}`}
                  aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                  data-testid={`${testId}-header-${c.key}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(c.key)}
                    onKeyDown={(e) => { if (e.altKey && e.key === "ArrowLeft") { e.preventDefault(); moveBy(c.key, -1); } if (e.altKey && e.key === "ArrowRight") { e.preventDefault(); moveBy(c.key, 1); } }}
                    className="w-full flex items-center gap-1 px-2.5 py-2 cursor-grab active:cursor-grabbing hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#327de3] text-left"
                    title="Click to sort · drag to move · Alt+←/→ to move"
                    data-testid={`${testId}-sort-${c.key}`}
                  >
                    <span className="truncate">{c.label}</span>
                    {active && (sort.dir === "asc"
                      ? <CaretUp size={11} weight="bold" className="flex-shrink-0" data-testid={`${testId}-sort-asc`} />
                      : <CaretDown size={11} weight="bold" className="flex-shrink-0" data-testid={`${testId}-sort-desc`} />)}
                  </button>
                  <span
                    onMouseDown={(e) => startResize(e, c.key)}
                    className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-[#327de3]/40"
                    data-testid={`${testId}-resize-${c.key}`}
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-slate-400 text-sm" data-testid={`${testId}-empty`}>{emptyText}</td></tr>
          )}
          {sorted.map((r) => {
            const id = rowKey(r);
            const sel = selectedId === id;
            return (
              <tr
                key={id}
                onClick={() => onRowClick?.(r)}
                className={`border-b border-slate-100 ${onRowClick ? "cursor-pointer" : ""} transition-colors ${sel ? "bg-[#eaf2ff] shadow-[inset_3px_0_0_#327de3]" : "hover:bg-slate-50"}`}
                data-testid={`${testId}-row-${id}`}
                data-selected={sel ? "true" : undefined}
              >
                {cols.map((c) => (
                  <td key={c.key} className={`px-2.5 ${dense ? "py-1.5" : "py-2"} truncate text-slate-700`}>
                    {c.render ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
